import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  http, requestErrorMessage,
} from "@/lib/erp";
import { useAuth } from "@/context/AuthContext";
import {
  AlertCircle, ArrowDownToLine, BookCopy, Check, ChevronDown, FilePlus2,
  FileText, FolderOpen, LockKeyhole, Pencil, Plus, Printer, Search, ShieldCheck,
  Trash2, Upload, X,
} from "lucide-react";
import { toast } from "sonner";
import {
  BUILTIN_LETTERS, EMPTY_LETTER, dataUrlBase64, dateInput, readFileDataUrl,
  hasAttachment, saveBlob, validateUpload,
} from "@/lib/legalLetters";
import "./legal-letters.css";

const today = () => new Date().toLocaleDateString("en-CA");
const freshDraft = (type = "") => ({ ...EMPTY_LETTER, letter_type: type, letter_date: today() });
const displayDate = (value) => value ? new Date(`${String(value).slice(0, 10)}T00:00:00`).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const attachmentName = (attachment) => attachment?.filename || attachment?.name || "";
const stripTemplate = (record) => ({
  letter_type: record.letter_type || "",
  title: record.title || "",
  letter_date: today(),
  recipient_name: record.recipient_name || "",
  recipient_address: record.recipient_address || "",
  subject: record.subject || "",
  content: record.content || "",
  signatory_name: record.signatory_name || "",
  signatory_designation: record.signatory_designation || "",
  signature: record.signature || null,
  attachment: record.attachment || null,
});
const employeeRecipient = (employee) => ({
  employee_id: employee?.id ?? null,
  recipient_name: employee?.name || "",
  recipient_address: [employee?.address, employee?.city].filter(Boolean).join(", "),
});

function Modal({ title, onClose, children, wide = false, className = "" }) {
  useEffect(() => {
    const onKey = (event) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return <div className={`ll-modal-backdrop ${className.includes("ll-view-modal") ? "ll-print-backdrop" : ""}`} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className={`ll-modal ${wide ? "ll-modal-wide" : ""} ${className}`} role="dialog" aria-modal="true" aria-label={title}>
      <header className="ll-modal-header"><div><p className="ll-overline">VGRUPS · PRIVATE HR RECORD</p><h2>{title}</h2></div>
        <button className="ll-icon-button" type="button" aria-label="Close dialog" onClick={onClose}><X size={18} /></button></header>
      {children}
    </section>
  </div>;
}

function Field({ label, children, className = "" }) {
  return <label className={`ll-field ${className}`}><span>{label}</span>{children}</label>;
}

function FilePicker({ label, fileName, accept, onChange, onRemove, hint, inputRef }) {
  return <div className="ll-file-box">
    <div className="ll-file-head"><strong>{label}</strong><span>{hint}</span></div>
    {fileName ? <div className="ll-file-selected"><FileText size={17} /><span title={fileName}>{fileName}</span>
      <button type="button" className="ll-text-action ll-danger-text" onClick={onRemove} aria-label={`Remove ${label.toLowerCase()}`}>Remove</button></div>
      : <label className="ll-file-choose"><Upload size={16} /><span>Choose file</span><input ref={inputRef} type="file" accept={accept} onChange={onChange} /></label>}
  </div>;
}

function LetterFields({ form, patch, types, onTypeChange, onSignature, onAttachment, signatureInput, attachmentInput, removeSignature, removeAttachment, busy }) {
  return <div className="ll-form-scroll">
    <div className="ll-form-section">
      <div className="ll-section-title"><span>01</span><div><h3>Letter details</h3><p>Keep the record identifiable and current.</p></div></div>
      <div className="ll-form-grid">
        <Field label="Letter type *"><select value={form.letter_type} onChange={(e) => onTypeChange(e.target.value)} required>
          <option value="">Choose a type</option>{types.map((type) => <option key={type.id} value={type.name}>{type.name}</option>)}
        </select></Field>
        <Field label="Letter date *"><input type="date" value={dateInput(form.letter_date)} onChange={(e) => patch("letter_date", e.target.value)} required /></Field>
        <Field label="Title *" className="ll-span-2"><input maxLength={120} value={form.title} onChange={(e) => patch("title", e.target.value)} placeholder="A clear title for this letter" required /></Field>
        <Field label="Recipient name"><input maxLength={120} value={form.recipient_name} onChange={(e) => patch("recipient_name", e.target.value)} placeholder="Employee or addressee" /></Field>
        <Field label="Subject"><input maxLength={240} value={form.subject} onChange={(e) => patch("subject", e.target.value)} placeholder="Letter subject" /></Field>
        <Field label="Recipient address" className="ll-span-2"><textarea rows="2" value={form.recipient_address} onChange={(e) => patch("recipient_address", e.target.value)} placeholder="Address (optional)" /></Field>
      </div>
    </div>
    <div className="ll-form-section">
      <div className="ll-section-title"><span>02</span><div><h3>Letter wording</h3><p>Plain text is preserved exactly in the print view.</p></div></div>
      <Field label="Editable content"><textarea className="ll-content-input" maxLength={30000} value={form.content} onChange={(e) => patch("content", e.target.value)} placeholder="Write or paste the letter wording here. New lines are retained." />
        <small className="ll-character-count">{form.content.length.toLocaleString()} / 30,000 characters</small></Field>
      <div className="ll-form-grid ll-signatory-grid">
        <Field label={`Signatory name${form.content.trim() ? " *" : ""}`}><input maxLength={120} value={form.signatory_name} onChange={(e) => patch("signatory_name", e.target.value)} placeholder="Authorized person's name" required={!!form.content.trim()} /></Field>
        <Field label="Signatory title"><input maxLength={120} value={form.signatory_designation} onChange={(e) => patch("signatory_designation", e.target.value)} placeholder="e.g. Human Resources Manager" /></Field>
      </div>
      <div className="ll-upload-grid">
        <FilePicker label="Signature image (optional)" fileName={form.signature ? "Signature image selected" : ""} accept="image/png,image/jpeg" onChange={onSignature} onRemove={removeSignature} inputRef={signatureInput} hint="PNG or JPEG · up to 200 KB" />
        <FilePicker label="Existing letter (optional)" fileName={attachmentName(form.attachment)} accept=".pdf,.docx,.png,.jpg,.jpeg,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg" onChange={onAttachment} onRemove={removeAttachment} inputRef={attachmentInput} hint="PDF, DOCX, PNG or JPEG · up to 5 MB" />
      </div>
      {busy && <div className="ll-inline-progress" role="status">Reading selected file…</div>}
    </div>
    <div className="ll-review-note"><ShieldCheck size={17} /><span>Review wording before issuing. These drafts are editable starting points, not legal compliance guidance.</span></div>
  </div>;
}

export default function LegalLetters() {
  const { can } = useAuth();
  const canManageUsers = can("manage_users");
  const [searchParams, setSearchParams] = useSearchParams();
  const [letters, setLetters] = useState([]);
  const [types, setTypes] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [editor, setEditor] = useState(null);
  const [form, setForm] = useState(freshDraft());
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);
  const [readingFile, setReadingFile] = useState(false);
  const [view, setView] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [templateEditor, setTemplateEditor] = useState(null);
  const [templateForm, setTemplateForm] = useState(freshDraft());
  const [templateError, setTemplateError] = useState("");
  const [templateSaving, setTemplateSaving] = useState(false);
  const [typesOpen, setTypesOpen] = useState(false);
  const [newType, setNewType] = useState("");
  const [typeSaving, setTypeSaving] = useState(false);
  const [downloading, setDownloading] = useState("");
  const [employeeContext, setEmployeeContext] = useState(null);
  const [employeeLoadingId, setEmployeeLoadingId] = useState("");
  const [employeeLoadError, setEmployeeLoadError] = useState("");
  const [queryError, setQueryError] = useState("");
  const signatureInput = useRef(null);
  const attachmentInput = useRef(null);
  const handledQueryRef = useRef("");

  const loadAll = useCallback(async () => {
    setLoading(true); setLoadError("");
    const [letterResult, typeResult, templateResult, companyResult] = await Promise.allSettled([
      http.get("/legal-letters"), http.get("/legal-letter-types"),
      http.get("/legal-letter-templates"), http.get("/company"),
    ]);
    const errors = [];
    if (letterResult.status === "fulfilled" && Array.isArray(letterResult.value.data)) setLetters(letterResult.value.data);
    else errors.push(letterResult.status === "rejected" ? requestErrorMessage(letterResult.reason, "load letters") : "Letters returned an unexpected response.");
    if (typeResult.status === "fulfilled" && Array.isArray(typeResult.value.data)) setTypes(typeResult.value.data);
    else errors.push(typeResult.status === "rejected" ? requestErrorMessage(typeResult.reason, "load letter types") : "Letter types returned an unexpected response.");
    if (templateResult.status === "fulfilled" && Array.isArray(templateResult.value.data)) setTemplates(templateResult.value.data);
    else errors.push(templateResult.status === "rejected" ? requestErrorMessage(templateResult.reason, "load templates") : "Templates returned an unexpected response.");
    if (companyResult.status === "fulfilled") setCompany(companyResult.value.data || {});
    setLoadError(errors.join(" "));
    setLoading(false);
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const patchForm = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const patchTemplate = (key, value) => setTemplateForm((current) => ({ ...current, [key]: value }));
  const openNew = useCallback((type = "", employee = employeeContext) => {
    const preset = BUILTIN_LETTERS.find((draft) => draft.name === type || draft.id === type);
    setForm({ ...freshDraft(type), ...(preset ? {
      title: preset.title, subject: preset.subject, content: preset.content,
      signatory_designation: preset.signatory_designation || "",
    } : {}), ...(employee ? employeeRecipient(employee) : {}) });
    setEmployeeLoadError(""); setEmployeeLoadingId("");
    setFormError(""); setEditor({ mode: "create" });
  }, [employeeContext]);
  const changeLetterType = (value, target = "letter") => {
    const setter = target === "template" ? setTemplateForm : setForm;
    const patchError = target === "template" ? setTemplateError : setFormError;
    const current = target === "template" ? templateForm : form;
    const preset = BUILTIN_LETTERS.find((draft) => draft.name === value || draft.id === value);
    setter((previous) => ({
      ...previous, letter_type: value,
      ...(!current.content?.trim() && !current.title?.trim() && preset ? {
        title: preset.title, subject: preset.subject, content: preset.content,
        signatory_designation: preset.signatory_designation || "",
      } : {}),
    }));
    patchError("");
  };
  const openEdit = async (id) => {
    setFormError(""); setEmployeeContext(null); setEmployeeLoadError(""); setEmployeeLoadingId("");
    try {
      const { data } = await http.get(`/legal-letters/${id}`);
      const employeeId = data.employee_id ?? null;
      setEmployeeContext(data.employee || null);
      setForm({ ...freshDraft(data.letter_type), ...data, employee_id: employeeId, letter_date: dateInput(data.letter_date), signature: data.signature || null, attachment: data.attachment || null });
      setEditor({ mode: "edit", id: data.id });
      if (employeeId) {
        if (!canManageUsers) {
          setFormError("You need the manage_users permission to edit a letter linked to an employee.");
        } else {
          setEmployeeLoadingId(String(employeeId));
          try {
            const { data: employee } = await http.get(`/employees/${encodeURIComponent(employeeId)}`);
            if (!employee || String(employee.id) !== String(employeeId)) throw new Error("The employee response did not match the linked employee.");
            setEmployeeContext(employee);
          } catch (error) {
            const message = requestErrorMessage(error, "load the linked employee");
            setEmployeeLoadError(message); setFormError(`Could not verify the linked employee. ${message}`);
          } finally { setEmployeeLoadingId(""); }
        }
      }
    } catch (error) { toast.error(requestErrorMessage(error, "open this letter for editing")); }
  };
  const onFile = async (event, kind, target = "letter") => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const message = validateUpload(file, kind);
    if (message) { target === "template" ? setTemplateError(message) : setFormError(message); return; }
    target === "template" ? setTemplateError("") : setFormError(""); setReadingFile(true);
    try {
      const dataUrl = await readFileDataUrl(file);
      const attachment = { filename: file.name, content_type: file.type, data: dataUrlBase64(dataUrl) };
      if (target === "template") setTemplateForm((current) => ({ ...current, [kind]: kind === "signature" ? dataUrl : attachment }));
      else setForm((current) => ({ ...current, [kind]: kind === "signature" ? dataUrl : attachment }));
    } catch (error) { target === "template" ? setTemplateError(error.message || "Could not read this file.") : setFormError(error.message || "Could not read this file."); }
    finally { setReadingFile(false); }
  };
  const saveLetter = async (event) => {
    event.preventDefault(); setFormError("");
    if (form.employee_id && !canManageUsers) {
      setFormError("You need the manage_users permission to save a letter linked to an employee. No employee association was sent."); return;
    }
    if (form.employee_id && (employeeLoadError || String(employeeContext?.id) !== String(form.employee_id))) {
      setFormError(`Could not verify the linked employee. ${employeeLoadError || "Load the employee before saving."} Saving is blocked to protect the association.`); return;
    }
    if (!form.letter_type || !form.title.trim() || !form.letter_date) { setFormError("Choose a type, title and date before saving."); return; }
    if (!form.content.trim() && !form.attachment) { setFormError("Add letter wording or attach an existing letter."); return; }
    if (form.content.trim() && !form.signatory_name.trim()) { setFormError("Enter a signatory name when letter wording is provided."); return; }
    const payload = {
      letter_type: form.letter_type, title: form.title.trim(), letter_date: dateInput(form.letter_date),
      recipient_name: form.recipient_name.trim(), recipient_address: form.recipient_address.trim(),
      subject: form.subject.trim(), content: form.content, signatory_name: form.signatory_name.trim(),
      signatory_designation: form.signatory_designation.trim(), signature: form.signature || null,
      attachment: form.attachment || null,
    };
    if (form.employee_id && canManageUsers) payload.employee_id = form.employee_id;
    setSaving(true);
    try {
      const result = editor.mode === "edit"
        ? await http.put(`/legal-letters/${editor.id}`, payload)
        : await http.post("/legal-letters", payload);
      toast.success(editor.mode === "edit" ? "Letter updated" : "Letter saved");
      setEditor(null); await loadAll();
      if (result.data?.id) openView(result.data.id);
    } catch (error) { setFormError(requestErrorMessage(error, "save this letter")); }
    finally { setSaving(false); }
  };
  const openView = useCallback(async (id) => {
    setViewLoading(true);
    try { const { data } = await http.get(`/legal-letters/${id}`); setView(data); }
    catch (error) { toast.error(requestErrorMessage(error, "load this letter")); }
    finally { setViewLoading(false); }
  }, []);
  const queryString = searchParams.toString();
  useEffect(() => {
    if (!queryString) { handledQueryRef.current = ""; return; }
    const params = new URLSearchParams(queryString);
    const employeeId = params.get("employee_id");
    const letterId = params.get("letter_id");
    const hasEmployeeId = params.has("employee_id");
    const hasLetterId = params.has("letter_id");
    const key = `${employeeId ?? ""}|${letterId ?? ""}`;
    if ((!hasEmployeeId && !hasLetterId) || handledQueryRef.current === key) return;
    handledQueryRef.current = key;
    setSearchParams({}, { replace: true });

    if (letterId) {
      openView(letterId);
      return;
    }
    if (!employeeId) {
      setQueryError("An employee_id is required to create a linked letter.");
      return;
    }
    if (!canManageUsers) {
      setQueryError("You need the manage_users permission to create a letter linked to an employee. No employee association was sent.");
      return;
    }
    setQueryError(""); setEmployeeLoadError(""); setEmployeeContext(null);
    setEmployeeLoadingId(employeeId);
    (async () => {
      try {
        const { data: employee } = await http.get(`/employees/${encodeURIComponent(employeeId)}`);
        if (!employee || String(employee.id) !== String(employeeId)) {
          throw new Error("The employee response did not match the requested employee.");
        }
        setEmployeeContext(employee); setEmployeeLoadingId("");
        openNew("", employee);
      } catch (error) {
        const message = requestErrorMessage(error, "load this employee");
        setEmployeeLoadError(message); setEmployeeLoadingId("");
        setQueryError(`Could not load employee ${employeeId}. ${message} The linked letter cannot be saved until the employee is verified.`);
        setForm({ ...freshDraft(), employee_id: employeeId });
        setFormError(`Could not verify employee ${employeeId}. ${message} Saving this linked letter is blocked.`);
        setEditor({ mode: "create" });
      }
    })();
  }, [queryString, setSearchParams, canManageUsers, openNew, openView]);
  const deleteLetter = async (row) => {
    if (!window.confirm(`Delete “${row.title}”? This cannot be undone.`)) return;
    try { await http.delete(`/legal-letters/${row.id}`); toast.success("Letter deleted"); await loadAll(); }
    catch (error) { toast.error(requestErrorMessage(error, "delete this letter")); }
  };
  const downloadAttachment = async (id, kind, name) => {
    const key = `${kind}-${id}`; setDownloading(key);
    try {
      const base = kind === "template" ? "/legal-letter-templates" : "/legal-letters";
      const { data, headers } = await http.get(`${base}/${id}/attachment`, { responseType: "blob" });
      const fromHeader = headers?.["content-disposition"]?.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i)?.[1];
      const filename = name || (fromHeader ? decodeURIComponent(fromHeader.replace(/"/g, "")) : "letter-attachment");
      saveBlob(data, filename);
    } catch (error) { toast.error(requestErrorMessage(error, "download this attachment")); }
    finally { setDownloading(""); }
  };
  const openTemplates = async () => { setTemplatesOpen(true); };
  const openTemplateCreate = () => { setTemplateForm(freshDraft()); setTemplateError(""); setTemplateEditor({ mode: "create" }); };
  const openTemplateEdit = async (id) => {
    setTemplateError("");
    try {
      const { data } = await http.get(`/legal-letter-templates/${id}`);
      setTemplateForm({ ...freshDraft(data.letter_type), ...data, letter_date: dateInput(data.letter_date) || today(), signature: data.signature || null, attachment: data.attachment || null });
      setTemplateEditor({ mode: "edit", id: data.id });
    } catch (error) { toast.error(requestErrorMessage(error, "open this template")); }
  };
  const saveTemplate = async (event) => {
    event.preventDefault(); setTemplateError("");
    if (!templateForm.letter_type || !templateForm.title.trim() || !templateForm.name?.trim()) { setTemplateError("Template name, letter type and title are required."); return; }
    if (!templateForm.content.trim() && !templateForm.attachment) { setTemplateError("Add wording or attach a reusable existing letter."); return; }
    if (templateForm.content.trim() && !templateForm.signatory_name.trim()) { setTemplateError("Enter a signatory name when template wording is provided."); return; }
    const payload = {
      name: templateForm.name.trim(), letter_type: templateForm.letter_type, title: templateForm.title.trim(),
      letter_date: dateInput(templateForm.letter_date) || today(), recipient_name: (templateForm.recipient_name || "").trim(),
      recipient_address: (templateForm.recipient_address || "").trim(), subject: (templateForm.subject || "").trim(),
      content: templateForm.content || "", signatory_name: (templateForm.signatory_name || "").trim(),
      signatory_designation: (templateForm.signatory_designation || "").trim(), signature: templateForm.signature || null,
      attachment: templateForm.attachment || null,
    };
    setTemplateSaving(true);
    try {
      if (templateEditor.mode === "edit") await http.put(`/legal-letter-templates/${templateEditor.id}`, payload);
      else await http.post("/legal-letter-templates", payload);
      toast.success(templateEditor.mode === "edit" ? "Template updated" : "Template saved");
      setTemplateEditor(null);
      const { data } = await http.get("/legal-letter-templates"); setTemplates(Array.isArray(data) ? data : []);
    } catch (error) { setTemplateError(requestErrorMessage(error, "save this template")); }
    finally { setTemplateSaving(false); }
  };
  const reuseTemplate = async (id) => {
    try {
      const { data } = await http.get(`/legal-letter-templates/${id}`);
      const linkedEmployeeId = employeeContext?.id ?? form.employee_id;
      const employeeFields = employeeContext ? employeeRecipient(employeeContext) : (linkedEmployeeId ? { employee_id: linkedEmployeeId } : {});
      setForm({ ...stripTemplate(data), ...employeeFields }); setFormError(""); setTemplatesOpen(false); setTemplateEditor(null); setEditor({ mode: "create" });
      toast.success("Template copied into a new letter. Review the details before saving.");
    } catch (error) { toast.error(requestErrorMessage(error, "reuse this template")); }
  };
  const deleteTemplate = async (item) => {
    if (!window.confirm(`Delete template “${item.name}”? Saved letters are not affected.`)) return;
    try { await http.delete(`/legal-letter-templates/${item.id}`); toast.success("Template deleted"); setTemplates((current) => current.filter((row) => row.id !== item.id)); }
    catch (error) { toast.error(requestErrorMessage(error, "delete this template")); }
  };
  const createType = async (event) => {
    event.preventDefault(); const name = newType.trim();
    if (!name || name.length > 120) return;
    setTypeSaving(true);
    try {
      const { data } = await http.post("/legal-letter-types", { name });
      setTypes((current) => [...current.filter((item) => item.id !== data.id), data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewType(""); toast.success("Letter type added");
    } catch (error) { toast.error(requestErrorMessage(error, "create letter type")); }
    finally { setTypeSaving(false); }
  };
  const deleteType = async (type) => {
    if (type.builtin) return;
    if (!window.confirm(`Delete letter type “${type.name}”? Types in use cannot be deleted.`)) return;
    try { await http.delete(`/legal-letter-types/${type.id}`); setTypes((current) => current.filter((item) => item.id !== type.id)); toast.success("Letter type deleted"); }
    catch (error) { toast.error(requestErrorMessage(error, "delete this type. Types used by saved letters cannot be deleted")); }
  };
  const saveLetterAsTemplate = async () => {
    if (!view) return;
    setTemplateForm({ ...stripTemplate(view), name: `${view.title} template` }); setTemplateError("");
    setView(null); setTemplateEditor({ mode: "create" });
  };
  const printLetter = () => window.print();

  const filteredLetters = useMemo(() => {
    const q = search.trim().toLowerCase();
    return letters.filter((letter) => (!typeFilter || letter.letter_type === typeFilter) &&
      (!q || [letter.letter_number, letter.title, letter.recipient_name, letter.letter_type, letter.subject].some((value) => String(value || "").toLowerCase().includes(q))));
  }, [letters, search, typeFilter]);
  const companyName = company.legal_name || company.name || localStorage.getItem("vg_company_name") || "VGRUPS";

  return <div className="legal-letters">
    <div className="ll-page">
      <div className="ll-topline"><span>PEOPLE OPERATIONS <i /> PRIVATE DOCUMENTS</span><span className="ll-secure"><LockKeyhole size={13} /> Company workspace only</span></div>
      <div className="ll-hero">
        <div className="ll-hero-copy">
          <div className="ll-eyebrow"><span className="ll-eyebrow-mark"></span> LEGAL · EMPLOYMENT</div>
          <h1>Letters, kept<br /><em>in order.</em></h1>
          <p>Prepare private employment correspondence, keep authorized documents close, and reuse approved starting points.</p>
          <div className="ll-hero-actions">
            <button className="ll-primary" onClick={() => openNew()}><Plus size={16} /> Create a letter</button>
            <button className="ll-secondary" onClick={openTemplates}><BookCopy size={16} /> Browse templates</button>
          </div>
          <div className="ll-starter-row" aria-label="Start from a built-in letter draft">
            {BUILTIN_LETTERS.filter((draft) => draft.id !== "other").map((draft) => <button type="button" key={draft.id} onClick={() => openNew(draft.name)}>{draft.name}<span>↗</span></button>)}
          </div>
        </div>
        <div className="ll-hero-art" aria-hidden="true">
          <div className="ll-art-orbit"></div><div className="ll-art-page"><div className="ll-art-stamp">VGRUPS<br /><small>PEOPLE OFFICE</small></div><span className="ll-art-line ll-art-line-long"></span><span className="ll-art-line"></span><span className="ll-art-line ll-art-line-mid"></span><span className="ll-art-gap"></span><span className="ll-art-line ll-art-line-long"></span><span className="ll-art-line ll-art-line-mid"></span><div className="ll-art-sign">Signed with care</div></div>
          <div className="ll-art-index">LL / 01—04</div>
        </div>
      </div>

      <div className="ll-caution"><AlertCircle size={16} /><span>Starter drafts are neutral and editable. <strong>Review wording before issuing.</strong> No legal compliance guarantees are implied.</span></div>
      {queryError && <div className="ll-error-banner" role="alert"><span>{queryError}</span></div>}
      {loadError && <div className="ll-error-banner" role="alert"><span>{loadError}</span><button onClick={loadAll}>Retry loading</button></div>}

      <div className="ll-section-head">
        <div><div className="ll-eyebrow">DOCUMENT REGISTER</div><h2>Saved letters <span>{letters.length.toString().padStart(2, "0")}</span></h2></div>
        <button className="ll-link-button" onClick={() => setTypesOpen(true)}><ChevronDown size={15} /> Manage letter types</button>
      </div>
      <section className="ll-register">
        <div className="ll-toolbar">
          <label className="ll-search"><Search size={16} /><input aria-label="Search saved letters" placeholder="Search title, recipient or reference…" value={search} onChange={(e) => setSearch(e.target.value)} /></label>
          <label className="ll-select-wrap"><span>TYPE</span><select aria-label="Filter by letter type" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}><option value="">All types</option>{types.map((type) => <option key={type.id} value={type.name}>{type.name}</option>)}</select></label>
          <button className="ll-upload-button" onClick={() => openNew()}><FilePlus2 size={16} /> Upload / add existing</button>
        </div>
        {loading ? <div className="ll-skeleton-list" aria-label="Loading letters">{[1, 2, 3].map((item) => <div className="ll-skeleton-row" key={item}><i /><span /><span /><b /></div>)}</div>
          : filteredLetters.length ? <div className="ll-table-wrap"><table className="ll-table">
            <thead><tr><th>Reference</th><th>Letter</th><th>Recipient</th><th>Type</th><th>Date</th><th><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>{filteredLetters.map((row) => <tr key={row.id}>
              <td><span className="ll-reference">{row.letter_number || `LL-${String(row.id).padStart(4, "0")}`}</span></td>
              <td><button className="ll-row-title" onClick={() => openView(row.id)}>{row.title}</button><small>{row.subject || "Employment correspondence"}</small></td>
              <td>{row.recipient_name || <span className="ll-muted">—</span>}</td>
              <td><span className="ll-type-chip">{row.letter_type}</span></td>
              <td className="ll-date">{displayDate(row.letter_date)}</td>
              <td><div className="ll-row-actions">
                <button className="ll-icon-button" aria-label={`View ${row.title}`} title="View" onClick={() => openView(row.id)}><FileText size={16} /></button>
                <button className="ll-icon-button" aria-label={`Edit ${row.title}`} title="Edit" onClick={() => openEdit(row.id)}><Pencil size={15} /></button>
                <button className="ll-icon-button ll-delete" aria-label={`Delete ${row.title}`} title="Delete" onClick={() => deleteLetter(row)}><Trash2 size={15} /></button>
              </div></td>
            </tr>)}</tbody>
          </table></div>
          : <div className="ll-empty"><div className="ll-empty-icon"><FolderOpen size={23} /></div><h3>{letters.length ? "No letters match this view" : "A private place for important words."}</h3><p>{letters.length ? "Try another search or letter type." : "Create a letter from an editable draft, or upload an existing company document."}</p>
            {letters.length === 0 && <button className="ll-primary" onClick={() => openNew()}><Plus size={16} /> Add your first letter</button>}</div>}
        <div className="ll-register-footer"><span><LockKeyhole size={13} /> Access governed by your company permissions</span><button onClick={openTemplates}>Manage reusable templates <ArrowDownToLine size={14} /></button></div>
      </section>
      <footer className="ll-page-footer"><span>VGRUPS ERP <i /> PRIVATE EMPLOYMENT RECORDS</span><span>Documents remain within your authenticated workspace.</span></footer>
    </div>

    {editor && <Modal title={editor.mode === "edit" ? "Edit saved letter" : "Create a letter"} wide onClose={() => !saving && setEditor(null)} className="ll-editor-modal">
      <form onSubmit={saveLetter}>
        {form.employee_id && <div className="ll-review-note" role="status"><ShieldCheck size={17} /><span><strong>Linked employee · read only</strong><br />
          Employee number: {employeeContext?.employee_number || "Not available"} · Name: {employeeContext?.name || form.recipient_name || "Not available"}
          {employeeLoadingId && <><br />Loading employee details…</>}
        </span></div>}
        <LetterFields form={form} patch={patchForm} types={types} onTypeChange={(value) => changeLetterType(value)} busy={readingFile}
          signatureInput={signatureInput} attachmentInput={attachmentInput}
          onSignature={(event) => onFile(event, "signature")} onAttachment={(event) => onFile(event, "attachment")}
          removeSignature={() => patchForm("signature", null)} removeAttachment={() => patchForm("attachment", null)} />
        {formError && <div className="ll-form-error" role="alert">{formError}</div>}
        <div className="ll-modal-actions"><button type="button" className="ll-secondary" onClick={() => setEditor(null)} disabled={saving}>Cancel</button>
          <button className="ll-primary" type="submit" disabled={saving || readingFile || !form.title.trim() || !form.letter_type || (!form.content.trim() && !form.attachment) || (!!form.content.trim() && !form.signatory_name.trim())}>
            {saving ? "Saving…" : editor.mode === "edit" ? "Save changes" : "Save letter"} <Check size={16} /></button></div>
      </form>
    </Modal>}

    {viewLoading && <div className="ll-modal-backdrop"><div className="ll-loading-card" role="status"><div className="ll-loading-lines"><i /><i /><i /></div><span>Opening private letter…</span></div></div>}
    {view && <Modal title={view.title || "Letter details"} wide onClose={() => setView(null)} className="ll-view-modal">
      <div className="ll-view-toolbar no-print"><div><span className="ll-type-chip">{view.letter_type}</span><span className="ll-reference">{view.letter_number || "Saved letter"}</span></div>
        <div className="ll-view-actions">
          {view.attachment && <button className="ll-secondary ll-small-button" onClick={() => downloadAttachment(view.id, "letter", attachmentName(view.attachment))} disabled={downloading === `letter-${view.id}`}><ArrowDownToLine size={15} />{downloading === `letter-${view.id}` ? "Downloading…" : "Download original"}</button>}
          <button className="ll-secondary ll-small-button" onClick={() => { const data = view; setView(null); openEdit(data.id); }}><Pencil size={15} /> Edit</button>
          <button className="ll-secondary ll-small-button" onClick={saveLetterAsTemplate}><BookCopy size={15} /> Save as template</button>
          <button className="ll-primary ll-small-button" onClick={printLetter}><Printer size={15} /> Print / PDF</button>
        </div>
      </div>
      <div className="ll-paper-wrap">
        <article id="print-area" className="ll-paper">
          <header className="ll-paper-head"><div className="ll-paper-brand"><span className="ll-brand-symbol">V</span><div><strong>{companyName}</strong><small>{company.address || company.city || "People & Culture"}</small>{company.gstin && <small>GSTIN {company.gstin}</small>}</div></div>
            <div className="ll-paper-number"><span>DOCUMENT</span><strong>{view.letter_number || "EMPLOYMENT LETTER"}</strong><small>{displayDate(view.letter_date)}</small></div></header>
          <div className="ll-paper-rule"></div>
          <div className="ll-paper-body">
            <div className="ll-paper-date">Date: {displayDate(view.letter_date)}</div>
            {view.recipient_name && <div className="ll-paper-recipient"><strong>{view.recipient_name}</strong>{view.recipient_address && <span>{view.recipient_address}</span>}</div>}
            <p className="ll-paper-kind">{view.letter_type}</p><h2>{view.title}</h2>
            {view.subject && <p className="ll-paper-subject"><strong>Subject:</strong> {view.subject}</p>}
            {view.content ? <div className="ll-paper-content">{view.content}</div> : <div className="ll-attachment-print-note">Original document attached: {attachmentName(view.attachment) || "available for authenticated download"}<br />The attached file is not transcribed in this preview.</div>}
            {view.content && <div className="ll-paper-signature">
              {view.signature && <img src={view.signature} alt="Signatory signature" />}
              <div className="ll-signature-rule"></div><strong>{view.signatory_name}</strong>
              {view.signatory_designation && <span>{view.signatory_designation}</span>}
              <small>{view.signatory_designation || (view.letter_type.toLowerCase().includes("resignation") ? "Employee" : `For ${companyName}`)}</small>
            </div>}
          </div>
          <footer className="ll-paper-footer"><span>{companyName} · Private employment correspondence</span><span>{view.letter_number || "VGRUPS"}</span></footer>
        </article>
      </div>
      <div className="ll-modal-actions no-print"><button className="ll-secondary" onClick={() => setView(null)}>Close</button><button className="ll-primary" onClick={printLetter}><Printer size={15} /> Print / Save as PDF</button></div>
    </Modal>}

    {templatesOpen && <Modal title="Reusable templates" onClose={() => setTemplatesOpen(false)} className="ll-template-list-modal">
      <div className="ll-template-intro"><p>Templates store reusable wording and optional attachments independently from your saved letters.</p><button className="ll-primary" onClick={openTemplateCreate}><Plus size={15} /> New template</button></div>
      <div className="ll-template-list">
        {templates.length ? templates.map((item) => <article className="ll-template-row" key={item.id}>
          <div className="ll-template-mark"><BookCopy size={17} /></div><div className="ll-template-info"><strong>{item.name}</strong><span>{item.letter_type} · {item.title}</span>{hasAttachment(item) && <small><FileText size={12} /> {attachmentName(item.attachment) || "Attachment saved"}</small>}</div>
          <div className="ll-template-actions"><button className="ll-mini-action ll-reuse" onClick={() => reuseTemplate(item.id)} title="Use template"><FilePlus2 size={15} /><span>Use</span></button>
            <button className="ll-mini-action" onClick={() => openTemplateEdit(item.id)} title="Edit template"><Pencil size={15} /></button>
            {hasAttachment(item) && <button className="ll-mini-action" onClick={() => downloadAttachment(item.id, "template", attachmentName(item.attachment))} title="Download attachment" disabled={downloading === `template-${item.id}`}><ArrowDownToLine size={15} /></button>}
            <button className="ll-mini-action ll-delete" onClick={() => deleteTemplate(item)} title="Delete template"><Trash2 size={15} /></button></div>
        </article>) : <div className="ll-template-empty"><BookCopy size={22} /><strong>No saved templates yet</strong><span>Save a useful starting point once, reuse it when you need it.</span></div>}
      </div>
      <div className="ll-modal-actions"><button className="ll-secondary" onClick={() => setTemplatesOpen(false)}>Done</button></div>
    </Modal>}

    {templateEditor && <Modal title={templateEditor.mode === "edit" ? "Edit template" : "Save reusable template"} wide onClose={() => !templateSaving && setTemplateEditor(null)} className="ll-editor-modal">
      <form onSubmit={saveTemplate}>
        <div className="ll-template-name-field"><Field label="Template name *"><input maxLength={120} value={templateForm.name || ""} onChange={(e) => patchTemplate("name", e.target.value)} placeholder="e.g. Sales team appointment" required /></Field></div>
        <LetterFields form={templateForm} patch={patchTemplate} types={types} onTypeChange={(value) => changeLetterType(value, "template")} busy={readingFile}
          signatureInput={signatureInput} attachmentInput={attachmentInput}
          onSignature={(event) => onFile(event, "signature", "template")} onAttachment={(event) => onFile(event, "attachment", "template")}
          removeSignature={() => patchTemplate("signature", null)} removeAttachment={() => patchTemplate("attachment", null)} />
        {templateError && <div className="ll-form-error" role="alert">{templateError}</div>}
        <div className="ll-modal-actions"><button type="button" className="ll-secondary" onClick={() => setTemplateEditor(null)} disabled={templateSaving}>Cancel</button>
          <button className="ll-primary" type="submit" disabled={templateSaving || readingFile || !templateForm.name?.trim() || !templateForm.title.trim() || !templateForm.letter_type || (!templateForm.content.trim() && !templateForm.attachment) || (!!templateForm.content.trim() && !templateForm.signatory_name.trim())}>{templateSaving ? "Saving…" : "Save template"} <Check size={16} /></button></div>
      </form>
    </Modal>}

    {typesOpen && <Modal title="Letter types" onClose={() => setTypesOpen(false)} className="ll-types-modal">
      <p className="ll-type-description">Use the standard types or create a company-specific category. Built-in types are protected; a custom type in use cannot be removed.</p>
      <form className="ll-add-type" onSubmit={createType}><input aria-label="New letter type name" maxLength={120} placeholder="Add a custom type…" value={newType} onChange={(e) => setNewType(e.target.value)} />
        <button className="ll-primary" disabled={!newType.trim() || typeSaving}>{typeSaving ? "Adding…" : <><Plus size={15} /> Add type</>}</button></form>
      <div className="ll-types-list">{types.map((type) => <div className="ll-type-row" key={type.id}><span className="ll-type-chip">{type.name}</span><span className="ll-type-kind">{type.builtin ? "Built in" : "Custom"}</span>
        <button className="ll-icon-button ll-delete" aria-label={`Delete ${type.name}`} disabled={type.builtin} title={type.builtin ? "Built-in types cannot be deleted" : "Delete type"} onClick={() => deleteType(type)}><Trash2 size={15} /></button></div>)}</div>
      <div className="ll-modal-actions"><button className="ll-secondary" onClick={() => setTypesOpen(false)}>Done</button></div>
    </Modal>}
  </div>;
}